import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import Paginat from "@/components/lib/paginat";
import FormInputField from "@/components/lib/from-input-field";
import LeavesTable from "@/components/backoffice/all-staff/leaves-settings/leaves-table";
import { Search } from "lucide-react";
import { fetchStaffLeaves } from "@/redux/slice/staff-management/staff-leave-slice";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "@/hooks/useAuth";
import TableSkeleton from "@/components/skeleton/table-skeletion";
import { usePagination } from "@/hooks/usePagination";
import { useSearch } from "@/hooks/useSearch";

const schema = z.object({
  date: z.string().optional(),
});

function LeavesSettings() {
  const { user } = useAuth();
  const dispatch = useDispatch();


  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      date: null,
    },
  });

  const { getLoading, leaves } = useSelector(
    (state) => state.staffManage.leaves
  );

  const [searchText, setSearchText] = useState("");
  const [perPage, setPerPage] = useState(10);
  const searchData = useSearch(
    leaves,
    searchText,
    ["staff_id", "name", "notes"],
    300
  );
  const { paginatedData, paginationInfo, onPageChange } = usePagination(
    searchData,
    perPage
  );

  const selectedDate = form.watch("date");

  useEffect(() => {
    if (!selectedDate || !user?.restaurant_id) return;

    const date = new Date(selectedDate);
    if (isNaN(date.getTime())) return;

    const formattedDate = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    const fetchLeavesData = async () => {
      const res = await dispatch(
        fetchStaffLeaves({
          restaurant_id: user.restaurant_id,
          date: formattedDate,
        })
      );
      if (fetchStaffLeaves.fulfilled.match(res)) {
        console.log("Data fetched!");
      } else if (fetchStaffLeaves.rejected.match(res)) {
        console.error(res.payload || "No data available for this month");
      }
    };

    fetchLeavesData();
  }, [user?.restaurant_id, dispatch, selectedDate]);

  if (getLoading) return <TableSkeleton links={backofficeLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Leave Settings" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        {
          name: "Leave Settings",
          path: "/backoffice/staff-management/leaves-settings",
        },
      ]}
    >
      <div className="container flex flex-col gap-4 mt-5">
        <div className="flex justify-between items-start md:items-center md:flex-row flex-col gap-5  w-full">
          <div className="flex gap-5 text-white">
            {/* <Select className="">
              <SelectTrigger className="w-[110px] border-gray-600">
                <SelectValue placeholder="Business" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Name</SelectLabel>
                  <SelectItem value="apple">XYZ</SelectItem>
                  <SelectItem value="banana">ABS</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Name</SelectLabel>
                  <SelectItem value="apple">Khulna</SelectItem>
                  <SelectItem value="banana">Dhaka</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Name</SelectLabel>
                  <SelectItem value="apple">Bonoul</SelectItem>
                  <SelectItem value="banana">Tunig star</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select> */}
          </div>
        </div>

        <div className="">
          <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
            <div className="w-[260px]">
              <Form {...form}>
                <form>
                  <FormInputField
                    name="date"
                    placeholder={"Select here for date"}
                    form={form}
                    type="date"
                    style="bg-cblack3 text-cwhite2"
                    label={"Search By Date"}
                  />
                </form>
              </Form>
            </div>
            <div className="flex flex-col  gap-1.5 justify-end items-end">
              <div className="flex gap-3 justify-center items-center">
                <div className="relative">
                  <Search
                    size={18}
                    className="text-cwhite3 absolute top-2 left-2"
                  />
                  <Input
                    placeholder="Search by Name or ID"
                    onChange={(e) => setSearchText(e.target.value)}
                    className="bg-cblack3 pl-8"
                  />
                </div>
                <div className="flex gap-1 justify-center items-center">
                  <h3 className="text-white">Show</h3>
                  <Select
                    onValueChange={(value) => setPerPage(Number(value))}
                    value={perPage.toString()}
                  >
                    <SelectTrigger className="w-[60px]">
                      <SelectValue placeholder="10" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="40">40</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <LeavesTable leaves={paginatedData} />
          </div>
          <div>
            <Paginat
              pagination={paginationInfo}
              onPageChange={onPageChange}
              className="mt-4"
            />
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default LeavesSettings;
